/**
 * Partner Section 2 deep link — `?pt=` → the i9FormId it points at (VTK-1918).
 *
 * The partner API hands the employer a link of the form `/sectiontwo?pt=<JWT>`. The token carries
 * the form id and nothing else useful; this reads it out so the app can forward to the real
 * Section 2 screen at `/section-2/:i9FormId`.
 *
 * **The token is a pointer, not a credential.** It is decoded, never verified — client-side
 * signature checking proves nothing, and no authority is derived from it. Reaching Section 2 still
 * requires an authenticated session and the `section2.update` permission, enforced by the
 * destination's own route guard exactly as it is for a user who navigated from the queue. A forged
 * or tampered token therefore buys nothing: at best it names a form id its holder still may not open.
 */

/** Base64url → JSON, without pulling in a JWT library for what is a plain payload read. */
function decodeSegment(segment: string): unknown {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  // atob yields one char per byte; decode those bytes as UTF-8 rather than latin1.
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** A GUID, so a malformed id can never be pasted into a route path. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The form id the link points at, or `undefined` when the token is absent, malformed, not a
 * Section 2 link, or carries no usable id — in which case the caller shows today's unauthorized
 * screen rather than routing somewhere arbitrary.
 */
export function i9FormIdFromPartnerSection2Token(rawToken?: string | null): string | undefined {
  const token = rawToken?.trim();
  if (!token) return undefined;

  const segments = token.split('.');
  if (segments.length !== 3) return undefined;

  try {
    const payload = decodeSegment(segments[1]) as { i9FormId?: unknown; purpose?: unknown };
    // Purpose is checked so a token minted for some other capability cannot be replayed into this
    // route. It is a sanity check on our own link format, not a security boundary.
    if (payload.purpose !== 'partner-section2') return undefined;
    const id = typeof payload.i9FormId === 'string' ? payload.i9FormId.trim() : '';
    return UUID.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}
